import { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { productionLogger } from '@/utils';

// Use compatible types with original useShopify
export interface ShopifyImage {
  id: string;
  url: string;
  altText: string | null;
  width?: number;
  height?: number;
}

export interface ShopifyVariant {
  id: string;
  title: string;
  availableForSale: boolean;
  quantityAvailable: number;
  price: {
    amount: string;
    currencyCode: string;
  };
  compareAtPrice?: {
    amount: string;
    currencyCode: string;
  };
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
  image?: ShopifyImage;
}

export interface ShopifyProduct {
  id: string;
  title: string;
  handle: string;
  description: string;
  productType: string;
  vendor: string;
  createdAt: string;
  updatedAt: string;
  tags: string[];
  availableForSale: boolean;
  totalInventory: number;
  images: ShopifyImage[];
  variants: ShopifyVariant[];
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
    maxVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  compareAtPriceRange?: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
    maxVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
}

export interface ShopifyCollection {
  id: string;
  title: string;
  handle: string;
  description: string;
  image?: ShopifyImage;
}

const SHOPIFY_STORE_DOMAIN = 'shop.bohostyleco.com';
const STOREFRONT_API_URL = `https://${SHOPIFY_STORE_DOMAIN}/api/2025-01/graphql.json`;
const STOREFRONT_ACCESS_TOKEN = '8656d15404e1ce2d169056b4e4d1ab89';

// Minimal cache for performance - prioritize fresh data
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes - matches main Shopify cache
const CACHE_KEYS = {
  products: 'shopify_products_selective_v1',
  collections: 'shopify_collections_selective_v1',
  featured: 'shopify_featured_v3',
};

// Global error tracking to prevent multiple toast messages
let globalErrorToastShown = false;
const resetGlobalErrorToast = () => {
  globalErrorToastShown = false;
};

function loadCache<T>(key: string): T | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as { ts: number; data: T };
    if (!entry?.ts || Date.now() - entry.ts > CACHE_TTL_MS) return null;
    return entry.data;
  } catch {
    return null;
  }
}

function saveCache<T>(key: string, data: T) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // ignore write errors
  }
}

const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));
const isRetriableStatus = (status: number) => status === 429 || status >= 500;

async function shopifyFetch<T>(
  query: string,
  variables: Record<string, any> = {},
  options: { retries?: number; retryDelayMs?: number } = {}
): Promise<T> {
  const { retries = 2, retryDelayMs = 300 } = options; // Reduced retry delay

  let attempt = 0;
  while (true) {
    attempt++;

    try {
      const response = await fetch(STOREFRONT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        const text = await response.text();
        if (isRetriableStatus(response.status) && attempt <= retries + 1) {
          const delay = retryDelayMs * Math.pow(1.5, attempt - 1); // Reduced backoff
          await sleep(delay);
          continue;
        }
        throw new Error(`Shopify HTTP ${response.status}: ${text.slice(0, 100)}`);
      }

      const json = await response.json();
      if (json.errors) {
        const message = json.errors[0]?.message || 'Unknown GraphQL error';
        if (attempt <= retries + 1) {
          const delay = retryDelayMs * Math.pow(1.5, attempt - 1);
          await sleep(delay);
          continue;
        }
        throw new Error(`Shopify GraphQL: ${message}`);
      }

      return json.data as T;
    } catch (err: any) {
      if (attempt <= retries + 1) {
        const delay = retryDelayMs * Math.pow(1.5, attempt - 1);
        await sleep(delay);
        continue;
      }
      throw err;
    }
  }
}

// Optimized queries with fewer fields for better performance
const FEATURED_PRODUCTS_QUERY = `
  query getFeaturedProducts($first: Int!) {
    products(first: $first, sortKey: UPDATED_AT, reverse: true) {
      edges {
        node {
          id
          title
          handle
          description
          productType
          vendor
          createdAt
          updatedAt
          tags
          availableForSale
          totalInventory
           images(first: 3) {
            edges {
              node {
                id
                url
                altText
              }
            }
          }
           variants(first: 5) {
            edges {
              node {
                id
                title
                availableForSale
                quantityAvailable
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                selectedOptions {
                  name
                  value
                }
                image {
                  id
                  url
                  altText
                }
              }
            }
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
`;

const PRODUCTS_COUNT_QUERY = `
  query getProductsCount {
    products(first: 1) {
      pageInfo {
        hasNextPage
      }
    }
  }
`;

const PAGINATED_PRODUCTS_QUERY = `
  query getPaginatedProducts($first: Int!, $after: String, $sortKey: ProductSortKeys) {
    products(first: $first, after: $after, sortKey: $sortKey, reverse: true) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          title
          handle
          description
          productType
          vendor
          createdAt
          updatedAt
          tags
          availableForSale
          totalInventory
          images(first: 5) {
            edges {
              node {
                id
                url
                altText
                width
                height
              }
            }
          }
          variants(first: 20) {
            edges {
              node {
                id
                title
                availableForSale
                quantityAvailable
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                selectedOptions {
                  name
                  value
                }
                image {
                  id
                  url
                  altText
                }
              }
            }
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
`;

const COLLECTIONS_QUERY = `
  query getCollections($first: Int!) {
    collections(first: $first) {
      edges {
        node {
          id
          title
          handle
          image {
            id
            url
            altText
          }
        }
      }
    }
  }
`;

/**
 * Optimized Shopify hook with selective/progressive loading
 * Only loads data when needed, with pagination and caching
 */
export const useSelectiveShopify = () => {
  const [featuredProducts, setFeaturedProducts] = useState<ShopifyProduct[]>([]);
  const [collections, setCollections] = useState<ShopifyCollection[]>([]);
  const [loadingFeatured, setLoadingFeatured] = useState(false);
  const [loadingCollections, setLoadingCollections] = useState(false);
  // Toast import handled above
  const hasToastedErrorRef = useRef(false);

  const loadFeaturedProducts = useCallback(async (forceRefresh: boolean = false) => {
    if (loadingFeatured) {
      return featuredProducts.length > 0 ? featuredProducts : [];
    }
    
    // Check cache first only if not forcing refresh
    if (!forceRefresh) {
      const cached = loadCache<ShopifyProduct[]>(CACHE_KEYS.featured);
      if (cached?.length) {
        setFeaturedProducts(cached);
        return cached;
      }
    }

    try {
      setLoadingFeatured(true);
      
      const data = await shopifyFetch<{ products: { edges: Array<{ node: any }> } }>(
        FEATURED_PRODUCTS_QUERY,
        { first: 24 } // Load more products to show latest inventory
      );

      const products: ShopifyProduct[] = data.products.edges.map(({ node }) => ({
        id: node.id,
        title: node.title,
        handle: node.handle,
        description: node.description || '',
        productType: node.productType || '',
        vendor: node.vendor || '',
        createdAt: node.createdAt || '',
        updatedAt: node.updatedAt || '',
        tags: node.tags || [],
        availableForSale: node.availableForSale,
        totalInventory: node.totalInventory || 0,
        images: node.images.edges.map(({ node: img }: any) => ({
          id: img.id,
          url: img.url,
          altText: img.altText,
        })),
        variants: node.variants.edges.map(({ node: variant }: any) => ({
          id: variant.id,
          title: variant.title,
          availableForSale: variant.availableForSale,
          quantityAvailable: variant.quantityAvailable || 0,
          price: variant.price,
          compareAtPrice: variant.compareAtPrice,
          selectedOptions: variant.selectedOptions || [],
          image: variant.image ? { id: variant.image.id, url: variant.image.url, altText: variant.image.altText } : undefined,
        })),
        priceRange: node.priceRange,
        compareAtPriceRange: node.compareAtPriceRange,
      }));

      setFeaturedProducts(products);
      saveCache(CACHE_KEYS.featured, products);
      return products;
    } catch (error: any) {
      productionLogger.error('loadFeaturedProducts ERROR:', error);
      productionLogger.error('Error loading featured products:', error);
      
      // Use global error tracking to prevent multiple toasts across all hook instances
      if (!globalErrorToastShown) {
        globalErrorToastShown = true;
        toast.error('Products temporarily unavailable', {
          description: 'Please try again in a moment',
        });
        // Reset after 30 seconds
        setTimeout(resetGlobalErrorToast, 30000);
      }
      return [];
    } finally {
      setLoadingFeatured(false);
    }
  }, [loadingFeatured, featuredProducts.length]);

  // Load collections (lightweight)
  const loadCollections = useCallback(async () => {
    if (loadingCollections) return;

    const cached = loadCache<ShopifyCollection[]>(CACHE_KEYS.collections);
    if (cached?.length) {
      setCollections(cached);
      return cached;
    }

    try {
      setLoadingCollections(true);
      const data = await shopifyFetch<{ collections: { edges: Array<{ node: any }> } }>(
        COLLECTIONS_QUERY,
        { first: 20 }
      );

      const collectionsData: ShopifyCollection[] = data.collections.edges.map(({ node }) => ({
        id: node.id,
        title: node.title,
        handle: node.handle,
        description: '',
        image: node.image ? {
          id: node.image.id,
          url: node.image.url,
          altText: node.image.altText,
        } : undefined,
      }));

      setCollections(collectionsData);
      saveCache(CACHE_KEYS.collections, collectionsData);
      return collectionsData;
    } catch (error: any) {
      productionLogger.error('Error loading collections:', error);
      return [];
    } finally {
      setLoadingCollections(false);
    }
  }, [loadingCollections]);

  // Get total products count (separate query for Storefront API)
  const getTotalProductCount = async (): Promise<number> => {
    try {
      // Since Storefront API doesn't expose total count directly, we need to estimate
      // by checking if there are more than X products using pagination
      let count = 0;
      let hasNextPage = true;
      let after: string | undefined = undefined;
      
      // Check in batches of 250 to estimate total quickly
      while (hasNextPage && count < 10000) { // Safety limit
        const data = await shopifyFetch<{ products: { edges: Array<any>, pageInfo: { hasNextPage: boolean, endCursor: string } } }>(
          PAGINATED_PRODUCTS_QUERY,
          { first: 250, after, sortKey: 'UPDATED_AT' }
        );
        
        count += data.products.edges.length;
        hasNextPage = data.products.pageInfo.hasNextPage;
        after = data.products.pageInfo.endCursor;
        
        // If we got less than 250, we've reached the end
        if (data.products.edges.length < 250) {
          break;
        }
      }
      
      return count;
    } catch (error) {
      productionLogger.error('Error getting total product count:', error);
      return 0;
    }
  };

  // Progressive product loading for /products page with proper cursor pagination
  const loadProducts = async (page: number = 1, perPage: number = 24, sortKey: string = 'UPDATED_AT', cursor?: string, includeTotalCount: boolean = false) => {
    try {
      // Use the provided cursor for pagination instead of calculating from page number
      const after = cursor;
      
      const data = await shopifyFetch<{ products: { edges: Array<{ node: any }>, pageInfo: { hasNextPage: boolean, endCursor: string } } }>(
        PAGINATED_PRODUCTS_QUERY,
        { first: perPage, after, sortKey }
      );

      const products: ShopifyProduct[] = data.products.edges.map(({ node }) => ({
        id: node.id,
        title: node.title,
        handle: node.handle,
        description: node.description || '',
        productType: node.productType || '',
        vendor: node.vendor || '',
        createdAt: node.createdAt,
        updatedAt: node.updatedAt,
        tags: node.tags || [],
        availableForSale: node.availableForSale,
        totalInventory: node.totalInventory || 0,
        images: node.images.edges.map(({ node: img }: any) => ({
          id: img.id,
          url: img.url,
          altText: img.altText,
        })),
        variants: node.variants.edges.map(({ node: variant }: any) => ({
          id: variant.id,
          title: variant.title,
          availableForSale: variant.availableForSale,
          quantityAvailable: variant.quantityAvailable || 0,
          price: variant.price,
          compareAtPrice: variant.compareAtPrice,
          selectedOptions: variant.selectedOptions || [],
        })),
        priceRange: node.priceRange,
        compareAtPriceRange: node.compareAtPriceRange,
      }));

      const result = {
        products,
        hasNextPage: data.products.pageInfo.hasNextPage,
        endCursor: data.products.pageInfo.endCursor,
        page,
        perPage,
        totalCount: includeTotalCount ? await getTotalProductCount() : undefined
      };

      return result;
    } catch (error: any) {
      productionLogger.error('Error loading products:', error);
      throw error;
    }
  };

  // Get single product by handle (unchanged for compatibility)
  const getProductByHandle = async (handle: string): Promise<ShopifyProduct | null> => {
    // Use original implementation from useShopify for single product fetching
    // This maintains compatibility while being selective
    const { useShopify } = await import('./useShopify');
    const shopify = useShopify();
    return shopify.getProductByHandle(handle);
  };

  return {
    // Selective data loading
    featuredProducts,
    collections,
    loadingFeatured,
    loadingCollections,
    
    // Progressive loading functions
    loadFeaturedProducts,
    loadCollections,
    loadProducts,
    getProductByHandle,
    
    // Utility functions
    clearCache: useCallback(() => {
      if (typeof localStorage === 'undefined') return;
      localStorage.removeItem(CACHE_KEYS.featured);
      localStorage.removeItem(CACHE_KEYS.collections);
      localStorage.removeItem(CACHE_KEYS.products);
    }, []),
  };
};
